import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Plus, Trash2, Users, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import LocationPicker from '../components/LocationPicker';
import EventLocationMap from '../components/EventLocationMap';
import { EVENT_CATEGORIES } from '../lib/eventData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CreateEventPage() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = !!editId;
  const { language } = useLanguage();
  const { user } = useAuth();
  const tr = (lv, en, ru) => (language === 'lv' ? lv : language === 'ru' ? ru : en);

  const [form, setForm] = useState({ title: '', description: '', category: '', address: '', event_date: '' });
  const [loc, setLoc] = useState({ name: '', region: '', lat: null, lng: null, id: null });
  const [slots, setSlots] = useState([{ role_title: '', count_needed: 1, pay: '', description: '' }]);
  const [submitting, setSubmitting] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  // Load existing event when editing.
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data: e } = await axios.get(`${API}/events/${editId}`, { withCredentials: true });
        setForm({
          title: e.title || '', description: e.description || '', category: e.category || '',
          address: e.address || '',
          event_date: e.event_date ? new Date(e.event_date).toISOString().slice(0, 16) : '',
        });
        setLoc({ name: e.city || e.location_name || '', region: e.region || '', lat: e.lat ?? null, lng: e.lng ?? null, id: e.location_id || null });
        setSlots((e.slots && e.slots.length)
          ? e.slots.map((s) => ({ role_title: s.role_title || '', count_needed: s.count_needed || 1, pay: s.pay ?? '', description: s.description || '' }))
          : [{ role_title: '', count_needed: 1, pay: '', description: '' }]);
      } catch (err) {
        toast.error(tr('Neizdevās ielādēt pasākumu', 'Failed to load event', 'Не удалось загрузить'));
        navigate('/events');
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  const geocodeAddress = async () => {
    const q = [form.address, loc.name].filter(Boolean).join(', ');
    if (!q || q.trim().length < 3) {
      toast.error(tr('Ieraksti adresi', 'Enter an address', 'Введите адрес'));
      return;
    }
    setGeocoding(true);
    try {
      const { data } = await axios.get(`${API}/geocode`, { params: { q } });
      if (data?.lat != null) {
        setLoc((l) => ({ ...l, lat: data.lat, lng: data.lng }));
        if (data.address) setForm((f) => ({ ...f, address: data.address }));
        toast.success(tr('Vieta atrasta — vari precizēt, pārvelkot kniepadatu', 'Found — drag the pin to fine-tune', 'Найдено — перетащите булавку'));
      } else {
        toast.error(tr('Adrese nav atrasta', 'Address not found', 'Адрес не найден'));
      }
    } catch (err) {
      toast.error(tr('Adrese nav atrasta', 'Address not found', 'Адрес не найден'));
    } finally { setGeocoding(false); }
  };

  useEffect(() => {
    if (!user || isEdit) return;
    setForm((f) => (f.address ? f : { ...f, address: user.address || '' }));
    setLoc((l) => (l.name ? l : {
      name: user.location_name || '', region: user.location_region_slug || user.region || '',
      lat: user.lat ?? null, lng: user.lng ?? null, id: null,
    }));
  }, [user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSlot = (i, k, v) => setSlots((s) => s.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const addSlot = () => setSlots((s) => [...s, { role_title: '', count_needed: 1, pay: '', description: '' }]);
  const removeSlot = (i) => setSlots((s) => s.filter((_, idx) => idx !== i));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.category) {
      toast.error(tr('Aizpildi obligātos laukus', 'Fill required fields', 'Заполните обязательные поля'));
      return;
    }
    if (!loc.region) {
      toast.error(tr('Izvēlies atrašanās vietu', 'Pick a location', 'Выберите местоположение'));
      return;
    }
    const validSlots = slots.filter((s) => s.role_title.trim().length >= 2);
    setSubmitting(true);
    try {
      const body = {
        title: form.title, description: form.description || null, category: form.category,
        region: loc.region, city: loc.name || null,
        location_id: loc.id, location_name: loc.name || null, lat: loc.lat, lng: loc.lng,
        address: form.address || null,
        event_date: form.event_date ? new Date(form.event_date).toISOString() : null,
        slots: validSlots.map((s) => ({
          role_title: s.role_title, count_needed: parseInt(s.count_needed) || 1,
          pay: s.pay ? parseFloat(s.pay) : null, description: s.description || null,
        })),
      };
      let data;
      if (isEdit) {
        ({ data } = await axios.put(`${API}/events/${editId}`, body, { withCredentials: true }));
        toast.success(tr('Pasākums atjaunināts!', 'Event updated!', 'Событие обновлено!'));
      } else {
        ({ data } = await axios.post(`${API}/events`, body, { withCredentials: true }));
        toast.success(tr('Pasākums izveidots!', 'Event created!', 'Событие создано!'));
      }
      navigate(`/events/${data.id}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : tr('Neizdevās saglabāt', 'Failed to save', 'Ошибка'));
      if (err?.response?.status === 401) navigate('/login');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="create-event-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate('/events')} className="flex items-center gap-1 text-[#5C5C5C] hover:text-[#D97736] mb-6 text-sm" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> {tr('Atpakaļ', 'Back', 'Назад')}
        </button>
        <div className="flex items-center gap-2 text-[#4A6B53] font-semibold mb-2">
          <Users className="w-5 h-5" /><span className="uppercase tracking-wider text-sm">{isEdit ? tr('Rediģēt pasākumu', 'Edit event', 'Редактировать') : tr('Jauns pasākums', 'New event', 'Новое событие')}</span>
        </div>
        <h1 className="text-3xl font-bold font-outfit text-[#121212] mb-6">{isEdit ? tr('Rediģē pasākumu un vietu', 'Edit event and location', 'Редактировать событие') : tr('Izveido pasākumu un lomas', 'Create an event and roles', 'Создайте событие и роли')}</h1>

        {loading ? (
          <div className="py-20 text-center text-[#5C5C5C]" data-testid="event-edit-loading">{tr('Ielādē...', 'Loading...', 'Загрузка...')}</div>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="bg-white border border-[#E5E4E0] rounded-xl p-6 space-y-4">
            <div>
              <Label>{tr('Nosaukums', 'Title', 'Название')} *</Label>
              <Input value={form.title} onChange={(e) => set('title', e.target.value)}
                placeholder={tr('Piem. Vasaras kāzas Siguldā', 'e.g. Summer wedding in Sigulda', 'Напр. Летняя свадьба')} data-testid="event-title-input" />
            </div>
            <div>
              <Label>{tr('Apraksts', 'Description', 'Описание')}</Label>
              <Textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={3} data-testid="event-desc-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>{tr('Kategorija', 'Category', 'Категория')} *</Label>
                <Select value={form.category} onValueChange={(v) => set('category', v)}>
                  <SelectTrigger data-testid="event-category-select"><SelectValue placeholder={tr('Izvēlies', 'Select', 'Выбрать')} /></SelectTrigger>
                  <SelectContent>{Object.entries(EVENT_CATEGORIES).map(([k, c]) => <SelectItem key={k} value={k}>{c[language] || c.lv}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>{tr('Datums un laiks', 'Date & time', 'Дата и время')}</Label>
                <Input type="datetime-local" value={form.event_date} onChange={(e) => set('event_date', e.target.value)} data-testid="event-date-input" />
              </div>
            </div>
            <div className="grid sm:grid-cols-1 gap-4">
              <div>
                <Label className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {tr('Atrašanās vieta', 'Location', 'Местоположение')} *</Label>
                <LocationPicker value={loc.name}
                  onSelect={(l) => setLoc({ name: l.name, region: l.region_slug || loc.region, lat: l.lat, lng: l.lng, id: l.id })}
                  placeholder={tr('Meklē pilsētu vai ciemu...', 'Search city or village...', 'Поиск города...')} testid="event-location-picker" />
                {loc.lat != null && <p className="text-xs text-[#4A6B53] mt-1">✓ {tr('Precīza atrašanās vieta iestatīta', 'Precise location set', 'Точное местоположение')}</p>}
              </div>
              <div>
                <Label htmlFor="event-address">{tr('Adrese / norises vieta', 'Address / venue', 'Адрес')}</Label>
                <div className="flex gap-2">
                  <Input id="event-address" value={form.address} onChange={(e) => set('address', e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); geocodeAddress(); } }}
                    placeholder={tr('Piem. Siguldas pils', 'e.g. Sigulda castle', 'Напр. замок')} data-testid="event-address-input" />
                  <Button type="button" variant="outline" onClick={geocodeAddress} disabled={geocoding}
                    className="shrink-0 border-[#E5E4E0] text-[#4A6B53]" data-testid="geocode-address-btn">
                    <MapPin className="w-4 h-4 mr-1" /> {geocoding ? tr('Meklē...', 'Finding...', 'Поиск...') : tr('Atrast', 'Find', 'Найти')}
                  </Button>
                </div>
              </div>
              <div>
                <Label className="flex items-center gap-1 mb-1">
                  {tr('Precīza vieta kartē', 'Precise spot on map', 'Точное место')}
                  <span className="text-xs font-normal text-[#9CA3AF]">— {tr('pārvelc kniepadatu', 'drag the pin', 'перетащите булавку')}</span>
                </Label>
                <EventLocationMap lat={loc.lat} lng={loc.lng}
                  onChange={(la, ln) => setLoc((l) => ({ ...l, lat: la, lng: ln }))} />
                {loc.lat != null && (
                  <p className="text-xs text-[#4A6B53] mt-1" data-testid="event-coords-label">
                    ✓ {tr('Koordinātas', 'Coordinates', 'Координаты')}: {loc.lat.toFixed(5)}, {loc.lng.toFixed(5)}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Role slots */}
          <div className="bg-white border border-[#E5E4E0] rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-outfit font-semibold text-[#121212]">{tr('Lomas, ko meklē', 'Roles you need', 'Нужные роли')} <span className="text-xs font-normal text-[#9CA3AF]">— {tr('neobligāti', 'optional', 'необязательно')}</span></h2>
              <Button type="button" size="sm" variant="outline" onClick={addSlot} className="border-[#E5E4E0] text-[#4A6B53]" data-testid="add-slot-btn">
                <Plus className="w-4 h-4 mr-1" /> {tr('Pievienot lomu', 'Add role', 'Добавить')}
              </Button>
            </div>
            <div className="space-y-4">
              {slots.map((s, i) => (
                <div key={i} className="p-4 rounded-lg border border-[#F3F2EE] bg-[#FAFAFA]" data-testid={`slot-row-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-[#5C5C5C] font-medium">{tr('Loma', 'Role', 'Роль')} #{i + 1}</span>
                    {slots.length > 1 && (
                      <button type="button" onClick={() => removeSlot(i)} data-testid={`remove-slot-${i}`}><Trash2 className="w-4 h-4 text-red-500" /></button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-2">
                      <Input value={s.role_title} onChange={(e) => setSlot(i, 'role_title', e.target.value)}
                        placeholder={tr('Piem. Fotogrāfs', 'e.g. Photographer', 'Напр. Фотограф')} data-testid={`slot-title-${i}`} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input type="number" min="1" value={s.count_needed} onChange={(e) => setSlot(i, 'count_needed', e.target.value)}
                        placeholder={tr('Skaits', 'Qty', 'Кол-во')} data-testid={`slot-count-${i}`} />
                      <Input type="number" min="0" value={s.pay} onChange={(e) => setSlot(i, 'pay', e.target.value)}
                        placeholder="€" data-testid={`slot-pay-${i}`} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Button type="submit" disabled={submitting} className="w-full bg-[#4A6B53] hover:bg-[#3d5944] text-white" data-testid="submit-event-btn">
            {submitting ? tr('Saglabā...', 'Saving...', 'Сохраняем...') : (isEdit ? tr('Saglabāt izmaiņas', 'Save changes', 'Сохранить') : tr('Publicēt pasākumu', 'Publish event', 'Опубликовать'))}
          </Button>
        </form>
        )}
      </div>
    </div>
  );
}
